import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { OptionMenu } from 'src/components/OptionMenu/OptionMenu';
import {
    OrganizationModuleType,
    isOrganizationModuleEnabled,
} from 'src/domain/organization';
import { useCurrentOrganization } from 'src/view/hooks';
import { AnalyticsMarker } from 'src/components/AnalyticsMarker/AnalyticsMarker';
import { MonthPeriodFilter } from 'src/components/MonthPeriodFilter/MonthPeriodFilter';
import { currentNorwegianMonth } from 'src/utils/norwegianMonth';
import { AnalyticsSourceChip } from './AnalyticsSourceChip';
import {
    MessageReadFilterTabs,
    messageReadFilterOptions,
} from './MessageReadFilterTabs';
import { useFilterRowLayout } from './useFilterRowLayout';
import { useMessageListUrlFilters } from './useMessageListUrlFilters';
import './messageListToolbar.css';

type MessageListFilters = ReturnType<typeof useMessageListUrlFilters>;

/** Month period for the main Messages list. `preview` renders its widest state for measuring. */
const MessageMonthFilter = ({
    filters,
    preview = false,
}: {
    filters: MessageListFilters;
    preview?: boolean;
}) => {
    const { t } = useTranslation();
    return (
        <MonthPeriodFilter
            preview={preview}
            label={t('modules.messages.filters.period')}
            allTimeLabel={t('modules.messages.filters.allTime')}
            selectedMonthLabel={t('modules.messages.filters.selectedMonth')}
            monthLabel={t('modules.messages.filters.month')}
            period={preview || filters.month ? 'month' : 'all'}
            month={filters.month ?? currentNorwegianMonth()}
            onPeriodChange={period =>
                filters.setMonth(
                    period === 'month' ? currentNorwegianMonth() : null,
                )
            }
            onMonthChange={filters.setMonth}
            marker={
                <AnalyticsMarker
                    hint={t('modules.messages.filters.analyticsHint')}
                />
            }
        />
    );
};

const ROW_GAP = 8;

/**
 * Tabs with the month period beside them when it fits in its widest state, otherwise on its own
 * line, so selecting a month never moves the page. Tabs become a dropdown like the calendar
 * view picker only when they do not fit on their own (mobile).
 */
export const MessageListToolbar = ({
    filters,
    noStaffReplyTotal,
}: {
    filters: MessageListFilters;
    noStaffReplyTotal?: number;
}) => {
    const { t } = useTranslation();
    const { filter, month, fromAnalytics, adminFilters } = filters;
    const { organization } = useCurrentOrganization();
    // Administrators (and support mode) can always open an enabled Analytics module.
    const canOpenAnalytics =
        adminFilters &&
        isOrganizationModuleEnabled(
            organization,
            OrganizationModuleType.analytics,
        );
    const row = useRef<HTMLDivElement>(null);
    const fullTabsCopy = useRef<HTMLDivElement>(null);
    const widestMonthCopy = useRef<HTMLDivElement>(null);
    const layout = useFilterRowLayout(
        row,
        fullTabsCopy,
        widestMonthCopy,
        ROW_GAP,
        adminFilters,
    );
    const showCount =
        filter === 'noStaffReply' && noStaffReplyTotal !== undefined;
    return (
        <>
            <div ref={row} className="message-list-filter-row">
                {layout.fullTabs ? (
                    <MessageReadFilterTabs
                        includeNoStaffReply
                        value={filter}
                        onChange={filters.setFilter}
                    />
                ) : (
                    <OptionMenu
                        label={t('modules.messages.filters.title')}
                        value={filter}
                        options={messageReadFilterOptions(t, {
                            includeNoStaffReply: true,
                        })}
                        onChange={filters.setFilter}
                    />
                )}
                {adminFilters && (
                    <div
                        className={
                            layout.monthInline
                                ? 'message-list-month'
                                : 'message-list-month message-list-month-own-line'
                        }
                    >
                        <MessageMonthFilter filters={filters} />
                    </div>
                )}
                <div
                    className="message-list-filter-measure-box"
                    aria-hidden="true"
                >
                    <div
                        ref={fullTabsCopy}
                        className="message-list-filter-measure"
                    >
                        <MessageReadFilterTabs
                            includeNoStaffReply
                            value={filter}
                            onChange={() => undefined}
                        />
                    </div>
                    {adminFilters && (
                        <div
                            ref={widestMonthCopy}
                            className="message-list-filter-measure"
                        >
                            <MessageMonthFilter filters={filters} preview />
                        </div>
                    )}
                </div>
            </div>
            {(fromAnalytics || showCount) && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    {fromAnalytics && (
                        <AnalyticsSourceChip
                            filterLabel={t(
                                `modules.messages.filters.${filter}`,
                            )}
                            month={month}
                            onClear={filters.clearAnalytics}
                            analyticsHref={
                                canOpenAnalytics
                                    ? `/app/analytics${
                                          month ? `?month=${month}` : ''
                                      }`
                                    : undefined
                            }
                        />
                    )}
                    {showCount && (
                        <p
                            className="text-sm text-mongooseGray"
                            aria-live="polite"
                        >
                            {t('modules.messages.filters.noStaffReplyCount', {
                                count: noStaffReplyTotal,
                            })}
                        </p>
                    )}
                </div>
            )}
        </>
    );
};
